import { AnySchema } from 'yup'
import {
  ComboboxGroupProps,
  ComboboxProps,
  InputProps,
  SingleDateProps,
} from '@node-space/ui-components'
import { SidePanelRowProps } from 'components/SidePanel/SidePanelSummary'
import {
  Business,
  CustomerAddress,
  CustomerDocumentStatusType,
  CustomerEntityType,
  CustomerPerson,
  CustomerShared,
  CustomerSummary,
  Individual,
  IndividualDetails,
} from 'types/customers'
import {
  EMBEDDED_CUSTOMER_AGREEMENT_STATUSES,
  EMBEDDED_CUSTOMER_STATUSES,
  RISK_SCORES,
  USE_CASES,
} from './constants'

export type EmbeddedCustomerStatus =
  (typeof EMBEDDED_CUSTOMER_STATUSES)[keyof typeof EMBEDDED_CUSTOMER_STATUSES]

export type EmbeddedCustomerAgreementStatus =
  (typeof EMBEDDED_CUSTOMER_AGREEMENT_STATUSES)[keyof typeof EMBEDDED_CUSTOMER_AGREEMENT_STATUSES]

export type RiskScore = (typeof RISK_SCORES)[keyof typeof RISK_SCORES]

export type UseCase = (typeof USE_CASES)[keyof typeof USE_CASES]

export type EmbeddedCustomerShared = CustomerShared<EmbeddedCustomerStatus>

export type EmbeddedCustomer = EmbeddedCustomerShared & CustomerSummary

export type EmbeddedCustomerAgreement = {
  status: EmbeddedCustomerAgreementStatus
  name: string
  description: string
  url: string
}

export type EmbeddedCustomerAssociate = {
  person: CustomerPerson
  title: string
}

export type UnifiedAssociateStatus = 'PENDING' | 'VERIFIED'

export type UnifiedAssociate = {
  id: string
  title: string
  body: string
  status: UnifiedAssociateStatus
}

export type EmbeddedCustomerVerification = {
  url: string
}

export type EmbeddedBusinessCustomer = Business & {
  associates?: EmbeddedCustomerAssociate[]
}

export type EmbeddedIndividualMontlyVolume = {
  amount: number
  currency: string
}

export type EmbeddedIndividualCdd = {
  employmentStatus: string
  sourceOfFunds: string
  pepStatus: string
  expectedMonthlyVolume: EmbeddedIndividualMontlyVolume
  intendedUseOfAccount: string
  estimatedYearlyIncome?: string
  employmentIndustrySector?: string
}

export type EmbeddedIndividualTaxIdentification = {
  number: string
  taxResidenceCountryCode?: string
}

export type EmbeddedIndividualContactInfo = {
  emailAddress: string
  phoneNumber: string
}

export type EmbeddedIndividualDetails = IndividualDetails & {
  contactInfo?: EmbeddedIndividualContactInfo
  taxIdentification?: EmbeddedIndividualTaxIdentification
}

export type EmbeddedIndividualCustomer = Individual & {
  details: EmbeddedIndividualDetails
  cdd?: EmbeddedIndividualCdd
}

export type EmbeddedCustomerBaseData = {
  useCase: UseCase
  riskScore?: RiskScore
  eddCompleted?: boolean
  externalReference?: string
}

export type EmbeddedCustomerData = EmbeddedCustomerBaseData &
  EmbeddedCustomerShared & {
    company?: EmbeddedBusinessCustomer
    individual?: EmbeddedIndividualCustomer
    verification: EmbeddedCustomerVerification
    pendingAgreementSessionUrl?: string
  }

export type RegulatorInformation = {
  regulatorName: string
  regulatorJurisdiction: string
}

export type PublicInformation = {
  listedExchange: string
  tickerSymbol: string
}

export type BusinessCustomerFormData = EmbeddedCustomerBaseData & {
  businessName: string
  businessDescription: string
  industry: string
  countryOfIncorporation: string
  stateCode?: string
  monthlyExpectedVolumes: string
  taxNumber?: string
  registrationNumber?: string
  businessOperationsStartDate?: string
  isRegulated?: string
  regulatorName?: string
  regulatorJurisdiction?: string
  isPubliclyListed?: string
  listedExchange?: string
  tickerSymbol?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
  isOperationalAddressDifferent?: string
  operationalAddressLine1?: string
  operationalAddressLine2?: string
  operationalAddressCity?: string
  operationalAddressPostalCode?: string
  operationalAddressCountryCode?: string
  operationalAddressStateCode?: string
}

export type BusinessCustomerFormKey = keyof BusinessCustomerFormData

export type IndividualCustomerData = {
  firstName: string
  lastName: string
  dateOfBirth: string
  emailAddress: string
  description: string
  nationality: string
}

export type IndividualCustomerFormData = EmbeddedCustomerBaseData &
  IndividualCustomerData &
  CustomerAddress &
  Omit<EmbeddedIndividualCdd, 'expectedMonthlyVolume'> & {
    taxIdentificationNumber?: string
    taxResidenceCountryCode?: string
    expectedMonthlyVolumeAmount: string
    expectedMonthlyVolumeCurrency: string
  }

export type IndividualCustomerFormKey = keyof IndividualCustomerFormData

export const INDIVIDUAL_COMBOBOX_KEYS = [
  'nationality',
  'countryCode',
  'stateCode',
  'taxResidenceCountryCode',
  'employmentStatus',
  'sourceOfFunds',
  'pepStatus',
  'expectedMonthlyVolumeCurrency',
  'useCase',
  'intendedUseOfAccount',
  'estimatedYearlyIncome',
  'employmentIndustrySector',
] as const satisfies IndividualCustomerFormKey[]

export type IndividualComboboxKey = (typeof INDIVIDUAL_COMBOBOX_KEYS)[number]
export type IndividualDateKey = 'dateOfBirth'
export type IndividualInputKey = Exclude<
  IndividualCustomerFormKey,
  IndividualComboboxKey | IndividualDateKey
>

export type IndividualCustomerFormFields = Partial<
  Record<IndividualInputKey, InputProps> &
    Record<IndividualComboboxKey, ComboboxProps> &
    Record<IndividualDateKey, SingleDateProps>
>

export type ConfirmFormData<TypeData> = {
  data: TypeData
  displayData?: Partial<TypeData>
}

export type ConfirmBusinessCustomerData = ConfirmFormData<BusinessCustomerFormData>
export type ConfirmIndividualCustomerData = ConfirmFormData<IndividualCustomerFormData>

export type ConfirmCustomerData = {
  business?: ConfirmBusinessCustomerData
  individual?: ConfirmIndividualCustomerData
}

export type ConfirmCustomerMethod<TypeData> = (customerTypeData: TypeData) => void
export type ConfirmBusinessCustomerMethod = ConfirmCustomerMethod<ConfirmBusinessCustomerData>
export type ConfirmIndividualCustomerMethod = ConfirmCustomerMethod<ConfirmIndividualCustomerData>

export type BusinessCustomerAddress = {
  countryCode: string
  stateCode?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
}

export type BusinessCustomerPayload = {
  name: string
  description: string
  industryReference: string
  monthlyExpectedVolumesReference: string
  address: BusinessCustomerAddress
  operationalAddress?: BusinessCustomerAddress
  taxNumber?: string
  registrationNumber?: string
  businessOperationsStartDate?: string
  isRegulated?: boolean
  regulatorInformation?: RegulatorInformation
  isPubliclyListed?: boolean
  publicInformation?: PublicInformation
}

export type IndividualCustomerPayload = IndividualCustomerData & {
  address: CustomerAddress
  cdd: EmbeddedIndividualCdd
  taxIdentification: EmbeddedIndividualTaxIdentification
}

export type EmbeddedCustomerPayload = EmbeddedCustomerBaseData & {
  type: CustomerEntityType
  company?: BusinessCustomerPayload
  individual?: IndividualCustomerPayload
}

export type EmbeddedCustomerPatchPayload = Partial<EmbeddedCustomerPayload>

export interface EmbeddedCustomerEntityDetailsProps<EntityDetails> {
  customer: EntityDetails
  sharedCustomerDetails: SidePanelRowProps[]
}

export interface CustomerDocument {
  reference: string
  externalReference?: string
  type: string
  name: string
  statusReason?: string
  description?: string
  status: CustomerDocumentStatusType
  subType: string
}

export type UploadedCustomerDocument = CustomerDocument & { isUploaded: true }

export type RequiredButNotUploadedDocument = CustomerDocument & { isUploaded: false }

export type MergedDocument = UploadedCustomerDocument | RequiredButNotUploadedDocument

export type DocumentType = string | string[]

export type RequiredDocument = {
  subType?: string
  nameKey: string
  type?: DocumentType
}

export const DASHBOARD_CARD_VISUAL_TYPES = {
  NEUTRAL: 'neutral',
  SUCCESS: 'success',
} as const

export type DashboardCardVisualType =
  (typeof DASHBOARD_CARD_VISUAL_TYPES)[keyof typeof DASHBOARD_CARD_VISUAL_TYPES]
export interface DashboardCardItem {
  step: string
  title: string
  queue: string
  type: DashboardCardVisualType
  isActive: boolean
}

export interface CustomerDashboardCards {
  cards: DashboardCardItem[]
  onCardClick: (step: string) => void
  loading: boolean
}
export interface DashboardCardState extends Omit<DashboardCardItem, 'isActive'> {
  status: EmbeddedCustomerStatus | null
}

export type DocumentRequirement = {
  docSetType: string
  types: string[]
}

export type AssociateRequirement = {
  roles: string[]
  customerReference?: string
  associateName?: string
  documents: DocumentRequirement[]
  questionnaires: string[]
  data?: string[]
}

export interface VerificationFailure {
  failureCode: string
  description: string
}

export type InfoRequired = {
  status: string
  infoRequired: {
    documents?: DocumentRequirement[]
    questionnaires?: string[]
    associates?: AssociateRequirement[]
    data?: string[]
  }
  verificationFailure?: {
    reasons: VerificationFailure[]
  }
}

export type BusinessCustomerFormSchema = Partial<Record<BusinessCustomerFormKey, AnySchema>>

export const BUSINESS_COMBOBOX_KEYS = [
  'countryOfIncorporation',
  'monthlyExpectedVolumes',
  'useCase',
  'stateCode',
  'regulatorJurisdiction',
  'operationalAddressCountryCode',
  'operationalAddressStateCode',
] as const satisfies BusinessCustomerFormKey[]

export const BUSINESS_COMBOBOX_GROUP_KEYS = ['industry'] as const

export const BUSINESS_RADIO_KEYS = [
  'isRegulated',
  'isPubliclyListed',
  'isOperationalAddressDifferent',
] as const satisfies BusinessCustomerFormKey[]

export type BusinessComboboxKey = (typeof BUSINESS_COMBOBOX_KEYS)[number]
export type BusinessComboboxGroupKey = (typeof BUSINESS_COMBOBOX_GROUP_KEYS)[number]
export type BusinessRadioKey = (typeof BUSINESS_RADIO_KEYS)[number]
export type BusinessInputKey = Exclude<
  BusinessCustomerFormKey,
  BusinessComboboxKey | BusinessComboboxGroupKey | BusinessRadioKey
>

export type BusinessDateKey = 'businessOperationsStartDate'

export type BusinessCustomerFormFields = Partial<
  Record<BusinessInputKey, InputProps> &
    Record<BusinessComboboxKey, ComboboxProps> &
    Record<BusinessComboboxGroupKey, ComboboxGroupProps> &
    Record<BusinessDateKey, SingleDateProps>
>
